import { User } from "@prisma-gen/client";
import type { PicowIssue, PicowValidation } from "@globals/picow";
import { UserState } from "@client/hooks/useUser";
import "./axios";
import { get, getAsync, postAsync, postAsyncSignal, putAsync } from "./axios";

export const reloadServer = () => getAsync<ApiResp<{ exitCode: number }>>("/api/reload");
export const getLogout = () => getAsync<ApiResp<null>>("/api/user/logout");

export const postSocketAuth = (data: SocketAuth) => postAsync<ApiResp<boolean>>("/api/sockets/auth", data);
export const getIfLoggedIn = get<ApiResp<Omit<User, "password">>>("/api/user/loggedIn");
export const getIfLoggedInAsync = () => getAsync<ApiResp<UserState>>("/api/user/loggedIn");
export const postLogin = (data: Login) =>
  postAsync<ApiResp<UserState & { sessionId: string }>>("/api/user/login", data);
export const postDark = (requestData: postDarkRequest) => postAsync<ApiResp<boolean>>("/api/user/dark", requestData);

export const postWebPushSubscribe = (data: PushSubscription) =>
  postAsync<ApiResp<boolean>>("/api/push/subscribe", data);
export const postWebPushUnsubscribe = (endpoint: string) =>
  postAsync<ApiResp<boolean>>("/api/push/unsubscribe", { endpoint });
export const getWebPushDevices = () => getAsync<ApiResp<WebPushDevice[]>>("/api/push/devices");
export const postWebPushAck = (notificationId: string, endpoint: string) =>
  postAsync<ApiResp<boolean>>("/api/push/ack", { notificationId, endpoint });
export const postWebPushSendNotification = () => postAsync<ApiResp<boolean>>("/api/push/send-notification");
export const getPushChannels = () => getAsync<ApiResp<PushChannelStatus>>("/api/push/channels");
// ntfy se nezapíná per uživatel — společný topic chodí v GET /api/push/channels.
export const postTelegramLink = () => postAsync<ApiResp<{ deepLink: string }>>("/api/push/channels/telegram/link");
export const postTelegramDisable = () => postAsync<ApiResp<boolean>>("/api/push/channels/telegram/disable");
export const postBarkSave = (deviceKey: string) => postAsync<ApiResp<boolean>>("/api/push/channels/bark", { deviceKey });
export const postBarkDisable = () => postAsync<ApiResp<boolean>>("/api/push/channels/bark/disable");

export const postRegister = (data: Register) => postAsync<ApiResp<UserState>>("/api/user/register", data);
export const postMqttData = (requestData: postMqttDataRequest) =>
  postAsync<ApiResp<HolyMQTTTransfer[]>>("/api/mqtt/data", requestData);

/**
 * Chart-optimized variant — server vrátí adaptive-bucketed data podle `pixelWidth`.
 * Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 1). Volá se z `useChartData` (Fáze 2).
 */
export const postChartData = (requestData: postMqttDataRequest & { pixelWidth: number }, signal: AbortSignal) =>
  postAsyncSignal<ApiResp<ChartDataResponse>>("/api/mqtt/data", requestData, signal);
/**
 * Batched chart variant — klient pošle PŘEDPOČÍTANÉ tile boundaries + resolution,
 * server vrátí všechny (topicId, tile) řádky v jednom response. Eliminuje N×M HTTP
 * request fan-out. Viz docs/plans/CHART-DATA-REFACTOR-FIX.md (Fáze 1-2).
 */
export const postChartDataBatched = (requestData: postChartBatchedRequest, signal: AbortSignal) =>
  postAsyncSignal<ApiResp<ChartBatchedResponse>>("/api/mqtt/data", requestData, signal);
export const postMqttToday = (requestData: postMqttTodayRequest) =>
  postAsync<ApiResp<postMqttTodayResponse>>("/api/mqtt/today", requestData);
export const postMqttStats = (requestData: postMqttStatsRequest) =>
  postAsync<ApiResp<postMqttStatsResponse>>("/api/mqtt/stats", requestData);
export const postMqttHourly = (requestData: postMqttHourlyRequest) =>
  postAsync<ApiResp<postMqttHourlyResponse>>("/api/mqtt/hourly", requestData);
export const getMqttFirstValues = () => getAsync<ApiResp<MQTTValueTransfer[]>>("/api/mqtt/firstValues");
export const getMqttTopicsMeta = () => getAsync<ApiResp<MQTTTopic[]>>("/api/mqtt/topics-meta");

export const getNotificationRules = () => getAsync<ApiResp<Rule[]>>("/api/rule/getRules");
export const postNotificationRules = (requestData: SetRules) =>
  postAsync<ApiResp<boolean>>("/api/rule/updateRules", requestData);

// --- PicoW ------------------------------------------------------------------
// Topic pro odeslání skládá SERVER z ověřeného configu; klient ho neposílá.
export const getPicowConfigs = () => getAsync<ApiResp<ConfigPicow[]>>("/api/picow/configs");

export const postPicowValidate = (requestData: { name: string; content: string }) =>
  postAsync<ApiResp<PicowValidation>>("/api/picow/validate", requestData);

export const putPicowConfig = (
  name: string,
  requestData: { content: string; baseSha256?: string; confirmIoOrder?: boolean }
) => putAsync<ApiResp<PicowSaveResult & { warnings: PicowIssue[] }>>(`/api/picow/${name}`, requestData);

export const getPicowHistory = (name: string) =>
  getAsync<ApiResp<PicowHistoryEntry[]>>(`/api/picow/${name}/history`);

export const getPicowHistoryFile = (name: string, file: string) =>
  getAsync<ApiResp<{ content: string }>>(`/api/picow/${name}/history/${file}`);

export const postPicowPush = (name: string) =>
  postAsync<ApiResp<{ topic: string; payload: string }>>(`/api/picow/${name}/push`, {});

export const getFoscams = () => getAsync<ApiResp<FoscamReact[]>>("/api/foscam/getFoscams");
